import { useClient, useMutation, useQuery } from 'urql';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CopyObjectCommand,
  DeleteObjectCommand,
  GetObjectCommand,
  PutObjectCommand,
  S3Client,
} from '@aws-sdk/client-s3';
import { v4 as uuid } from 'uuid';
import { useFlag } from '@unleash/proxy-client-react';

// local imports
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { isEmpty } from 'lodash';
import invariant from 'tiny-invariant';
import { useS3Client } from './s3';
import { usePropertyContext } from './property-resolving';
import { ResolvedOrder, getOrMakePool } from './order';
import { useActiveModels } from './active-models';
import {
  CreateHistoryEventDocument,
  GetCheckpointEventsForProjectDocument,
  GetEventGroupsForProjectDocument,
  GetEventsForProjectDocument,
  GetProjectDeepDocument,
  HistoryEventDeepFragment,
  HistoryEventGroupDeepFragment,
  HistoryEventInput,
  ProjectDeepFragment,
  UpdateHistoryEventDocument,
} from 'src/gql/graphql';
import {
  resolveOrderV2,
  getResolvedEntryForBackendCaching as shaveOffDatapoints,
} from 'src/domain/resolve-order-entry';
import { runWorkInBatches } from 'src/utils/promise-pool';
import { PropertyContext } from 'src/domain/property-operations';
import { ModelsMap, useViewer } from 'src/components/common/ForgeViewer';
import { Point } from 'src/domain/geometry/geometric-types';
import { dedupeOperations } from 'src/utils/dedupe-mutations';
import { useUserTenant } from 'src/services/auth-info';
import awsExports from 'src/aws-exports';
import { useProjectHistory } from 'src/components/project/project-history/hooks/useProjectHistory';
import { HISTORY_COLORS } from 'src/components/project/project-history/hooks/useDiffing';
import { hexToRgb } from 'src/utils/color-util';

const SPARKEL_EVENT_STATE_BUCKET = awsExports.events_state_bucket_name;

// Defines shape of the data stored in s3.
// We should consider using a schema validator to validate that the structure we get back is what we expcet

interface RawEventState {
  project: ProjectDeepFragment;
  propertyContext: Omit<PropertyContext, 'loadedModels'> & {
    loadedModels: Array<string>;
  };
}

export async function copyS3State(
  srcKey: string,
  destinationKey: string,
  s3Client: S3Client
) {
  invariant(
    SPARKEL_EVENT_STATE_BUCKET,
    'Bucket name is required to upload event state'
  );
  const command = new CopyObjectCommand({
    CopySource: `${SPARKEL_EVENT_STATE_BUCKET}/${srcKey}`,
    Bucket: SPARKEL_EVENT_STATE_BUCKET,
    Key: destinationKey,
  });
  await s3Client.send(command);
}

async function sendToS3(key: string, s3Client: S3Client, stateData: any) {
  invariant(
    SPARKEL_EVENT_STATE_BUCKET,
    'Bucket name is required to upload event state'
  );
  const command = new PutObjectCommand({
    Bucket: SPARKEL_EVENT_STATE_BUCKET,
    Body: JSON.stringify(stateData),
    ContentType: 'application/json',
    Key: key,
  });
  await s3Client.send(command);
}

function generateEventGroupId(groupedEvent: HistoryEventGroupDeepFragment) {
  return `${groupedEvent.author}-${groupedEvent.groupDate}-${groupedEvent.eventType}-${groupedEvent.resourceType}`;
}

// I suspect we will have the raw events and the resolved events hooks
// We could bundle them together but if we do, we might not be able to keep things lean
// we really need to keep this hook lean since it is going to be used in a million places

// This hook will be used then creating events
export function useEventHistory(projectId: string) {
  const s3Client = useS3Client(projectId);
  const [, createHistoryEventMutation] = useMutation(
    CreateHistoryEventDocument
  );
  const isHistoryEnabled = useFlag('enable-history');
  // Grab the project settings -> Polygon count and unit settings are what we are interested in
  // Grab the orders for project
  // from the query below, we should have the orders and the settings.
  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: { id: projectId },
  });

  // Grab the user tenant for bucket key
  const { tenant } = useUserTenant();

  // Grab the active models -> Log them at this point in time
  const { activeModels } = useActiveModels(projectId);

  // Grab the property context
  const { propertyContext, isAllPropertiesLoaded, error } =
    usePropertyContext(projectId);

  // Bare minimum data required to resolve orders
  const stateData = useMemo(() => {
    if (!isAllPropertiesLoaded) return null;
    const { loadedSparkelPropertiesIndex, ...rest } = propertyContext;
    return {
      project: projectData?.project,
      activeModels,
      propertyContext: {
        ...rest,
        loadedModels: Object.keys(propertyContext.loadedModels),
      },
    };
  }, [
    propertyContext,
    projectData?.project,
    isAllPropertiesLoaded,
    activeModels,
  ]);

  const logEvent = useCallback(
    async (input: Omit<HistoryEventInput, 'stateKey' | 'author'>) => {
      // TODO: Log this event to mixpanel as well if not already being logged
      // if already being logged, harmonize so that you do not send double events
      // TODO: Add a try catch block so that the errors in here do not bubble up
      // We might not need to inform people when an event recording fail. Might not matter form but crucial for us to know so log to sentry
      invariant(tenant, 'Tenant is required to log events');
      const key = `${
        tenant.group
      }/project/${projectId}/unresolved/${uuid()}.json`;
      // TODO: You might also want to wait until all the properties have loaded before initiating a write to s3
      if (!isAllPropertiesLoaded)
        throw new Error('Wait for all the properties to load before renaming');
      await sendToS3(key, s3Client, stateData);
      await createHistoryEventMutation({
        input: {
          historyEvent: {
            ...input,
            projectId,
            stateKey: key,
          },
        },
      });
    },
    [
      createHistoryEventMutation,
      isAllPropertiesLoaded,
      projectId,
      s3Client,
      stateData,
      tenant,
    ]
  );

  return {
    logEvent,
  };
}

// Hook to be used to fetch events for a given project
export function useProjectEventHistory(projectId: string) {
  const [cursor, setCursor] = useState<string | null>(null);
  const [imperativeEvents, setImperativeEvents] =
    useState<HistoryEventDeepFragment[]>();
  const [{ data, error, fetching }] = useQuery({
    query: GetEventsForProjectDocument,
    variables: { projectId, after: cursor },
  });

  const client = useClient();

  // Merge the declarative and imperatively fetched events
  const events = useMemo(() => {
    const declEvents = data?.historyEventsConnection?.nodes ?? [];
    const map = new Map<string, HistoryEventDeepFragment>();
    [...declEvents, ...(imperativeEvents ?? [])].forEach((item) => {
      map.set(item.id, item); // Later items will overwrite earlier ones
    });
    return Array.from(map.values());
  }, [imperativeEvents, data?.historyEventsConnection?.nodes]);

  // since the drawer is not (un)mounted when its open/closed state is toggled,
  // we need a way to refresh any events that might have been created without having to refresh
  const reloadEvents = useCallback(async () => {
    const result = await client
      .query(GetEventsForProjectDocument, { projectId, after: cursor })
      .toPromise();
    console.log(result);
    setImperativeEvents(result.data?.historyEventsConnection?.nodes);
  }, [cursor, projectId]);

  const loadMoreEvents = useCallback(() => {
    const nextCursor = data?.historyEventsConnection?.pageInfo.endCursor;
    if (nextCursor) {
      setCursor(nextCursor);
    }
  }, [data?.historyEventsConnection?.pageInfo.endCursor]);

  return {
    events,
    fetching,
    error,
    loadMoreEvents,
    reloadEvents,
  };
}

function getEventGroupsForState(
  groupsInState: HistoryEventGroupDeepFragment[],
  newGroups: HistoryEventGroupDeepFragment[],
  isLoadingMore: boolean
) {
  // if we loading more, we append, otherwise we replace what is currently in the state.
  if (!isLoadingMore) {
    // This means that we are loading, so we should always replace
    return newGroups;
  }
  // Dedupe the reloaded events
  const currentGroupIds = new Set(
    groupsInState.map((groupItem) => generateEventGroupId(groupItem))
  );
  const uniqueLoadedGroups = newGroups.filter(
    (groupItem) => !currentGroupIds.has(generateEventGroupId(groupItem))
  );
  return [...groupsInState, ...uniqueLoadedGroups];
}

const PAGE_SIZE = 10;
// Model this after the useProjectEventHistory hook above
export function useProjectEventGroupHistory(projectId: string) {
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const isLoadingMoreRef = useRef<boolean>(false);
  const [eventGroups, setEventGroups] = useState<
    HistoryEventGroupDeepFragment[]
  >([]);
  const [isFetchingGroups, setIsFetchingGroups] = useState<boolean>(false);
  const eventGroupsRef = useRef<HistoryEventGroupDeepFragment[]>(eventGroups);
  eventGroupsRef.current = eventGroups;
  const client = useClient();
  const reloadEventGroups = useCallback(async () => {
    // if we loading more, the page size should be page size, otherwise, set that to be equal to the number of events we have
    // this' so that we can reload all the events that we have in our state
    const computedPageSize = isLoadingMoreRef.current
      ? PAGE_SIZE
      : Math.max(eventGroupsRef.current.length, PAGE_SIZE);
    // if we are loading more, then the cursor should be set to the current cursor, otherwise it is set to null
    // this is so that we are able to load in more events from the cursor point
    // but also be able to refresh all the events from the beginning
    const computedCursor = isLoadingMoreRef.current ? cursor : null;
    setIsFetchingGroups(true);
    const result = await client
      .query(GetEventGroupsForProjectDocument, {
        projectId,
        afterCursor: computedCursor,
        pageSize: computedPageSize + 1,
      })
      .toPromise()
      .finally(() => setIsFetchingGroups(false));
    const reloadedGroupsRaw = result.data?.getHistoryEventGroups ?? [];
    // if (reloadedGroupsRaw.length <= 0) return [];
    const reloadedGroups = reloadedGroupsRaw.slice(0, computedPageSize);
    setHasNextPage(reloadedGroupsRaw.length > computedPageSize);
    const newGroupState = getEventGroupsForState(
      eventGroupsRef.current,
      reloadedGroups,
      isLoadingMoreRef.current
    );
    isLoadingMoreRef.current = false;
    // eventGroupsRef.current = newGroupState;
    setEventGroups(newGroupState);
    return newGroupState;
  }, [projectId, cursor]);

  const loadMoreGroups = useCallback(async () => {
    const nextCursor = eventGroups.at(-1)?.firstEvent?.createdAt;
    if (nextCursor) {
      isLoadingMoreRef.current = true;
      setCursor(nextCursor);
    }
  }, [eventGroups]);

  // Trigger fetch when cursor changes for loading more
  useEffect(() => {
    if (cursor && isLoadingMoreRef.current) {
      reloadEventGroups();
    }
  }, [cursor, reloadEventGroups]);

  return {
    hasNextPage,
    eventGroups,
    isFetchingGroups,
    loadMoreGroups,
    reloadEventGroups,
  };
}

export function useProjectCheckpointEventHistory(projectId: string | null) {
  const client = useClient();

  const [checkpointEvents, setCheckpointEvents] = useState<
    HistoryEventDeepFragment[]
  >([]);
  const [isFetchingGroups, setIsFetchingGroups] = useState(false);

  const reloadCheckpointEvents = useCallback(async () => {
    if (!projectId) {
      setCheckpointEvents([]);
      return [];
    }

    setIsFetchingGroups(true);

    try {
      const result = await client
        .query(GetCheckpointEventsForProjectDocument, { projectId })
        .toPromise();

      const events = result.data?.historyEvents ?? [];
      setCheckpointEvents(events);
      return events;
    } finally {
      setIsFetchingGroups(false);
    }
  }, [client, projectId]);

  // Auto-run when projectId changes
  useEffect(() => {
    if (projectId) {
      void reloadCheckpointEvents();
    } else {
      setCheckpointEvents([]);
    }
  }, [projectId, reloadCheckpointEvents]);

  return {
    checkpointEvents,
    isFetchingGroups,
    reloadCheckpointEvents,
  };
}

async function fetchS3StateString(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch with status: ${response.status}`);
  }

  const textStream = response.body!.pipeThrough(new TextDecoderStream());
  const reader = textStream.getReader();
  const chunks: Array<string> = [];

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    chunks.push(value!);
  }
  return chunks.join('');
}

// Hook to be used to resolve an event
export function useResolveEvent(projectId: string) {
  const s3Client = useS3Client(projectId);
  const { loadOverlayModels, viewer, unloadOverlayModel, clearColors } =
    useViewer();
  const { activeModels } = useActiveModels(projectId);
  const { tempModelOverlaysRef, isHistoryDrawerOpen, diff } =
    useProjectHistory();
  const { propertyContext, isAllPropertiesLoaded } =
    usePropertyContext(projectId);
  const propCtxRef = useRef<PropertyContext>(propertyContext);
  useEffect(() => {
    propCtxRef.current = propertyContext;
  }, [propertyContext]);
  const [, updateHistoryEvent] = useMutation(UpdateHistoryEventDocument);

  // effect to colorize the model
  // eslint-disable-next-line complexity
  useEffect(() => {
    const loadedModelsInDiff = diff?.['propertyContext.loadedModels'];
    if (
      isHistoryDrawerOpen &&
      viewer &&
      loadedModelsInDiff &&
      isAllPropertiesLoaded &&
      !isEmpty(propertyContext.loadedModels)
    ) {
      // when a model is deleted: pctx.loadedmodels.new will not contain the model but will be present in old
      // when a model is created: pctx.loadedmodels.new will contain a model that is not present in the old.
      const modelsInNew: Set<string> = new Set(
        JSON.parse(loadedModelsInDiff.new ?? '[]')
      );
      const modelsInOld: Set<string> = new Set(
        JSON.parse(loadedModelsInDiff.old ?? '[]')
      );
      // loadedModels also includes the historical models
      const modelEntries = Object.entries(propertyContext.loadedModels);
      for (const [modelUrn, modelInstance] of modelEntries) {
        const tree = modelInstance.getInstanceTree();
        if (!tree) continue;
        let rawColor = HISTORY_COLORS.MODIFIED;
        if (modelsInNew.has(modelUrn) && !modelsInOld.has(modelUrn)) {
          rawColor = HISTORY_COLORS.CREATED;
        } else if (!modelsInNew.has(modelUrn) && modelsInOld.has(modelUrn)) {
          rawColor = HISTORY_COLORS.DELETED;
        } else {
          continue;
        }
        const [r, g, b] = hexToRgb(rawColor);
        const color = new THREE.Vector4(r / 255, g / 255, b / 255, 1);
        const rootId = tree.getRootId();
        tree.enumNodeChildren(
          rootId,
          (dbid) => {
            viewer.setThemingColor(dbid, color, modelInstance, true);
          },
          true
        );
      }
    }
  }, [isHistoryDrawerOpen, diff, propertyContext.loadedModels, viewer]);

  // Method to get the URL of where the state is currently located
  const getStateUrl = useCallback(
    async (key: string) => {
      invariant(
        SPARKEL_EVENT_STATE_BUCKET,
        'Bucket name is required to fetch from events state bucket'
      );
      const command = new GetObjectCommand({
        Bucket: SPARKEL_EVENT_STATE_BUCKET,
        Key: key,
      });
      const signedUrl = await getSignedUrl(s3Client, command, {
        expiresIn: 3600,
      });
      return signedUrl;
    },
    [s3Client]
  );

  // Method to delete a state file
  const deleteS3State = useCallback(
    async (key: string) => {
      invariant(
        SPARKEL_EVENT_STATE_BUCKET,
        'Bucket name is required to delete from events state bucket'
      );
      const command = new DeleteObjectCommand({
        Bucket: SPARKEL_EVENT_STATE_BUCKET,
        Key: key,
      });
      await s3Client.send(command);
    },
    [s3Client]
  );

  // TODO: You need to test with a deleted model
  // essentially grab a model urn and attempt to load the model from here
  // then attempt to resolve a quantity from the loaded model
  const prepModels = useCallback(
    async (modelUrns: Array<string>) => {
      const modelsToLoad = [];
      const loadedModels: Record<string, Autodesk.Viewing.Model> = {};
      const ctxUrns = Object.keys(propCtxRef.current.loadedModels);
      const tempUrns = Object.keys(tempModelOverlaysRef.current);
      const seenSet = new Set([...ctxUrns, ...tempUrns]);
      for (const urn of modelUrns) {
        const found = seenSet.has(urn);
        if (found) {
          loadedModels[urn] =
            propCtxRef.current.loadedModels[urn] ||
            tempModelOverlaysRef.current[urn];
          continue;
        }
        modelsToLoad.push(urn);
      }
      if (modelsToLoad.length <= 0) {
        return loadedModels;
      }
      // we need to load the models that are missing, if we can't load the model, we throw an error?
      const tempModels = await loadOverlayModels(modelsToLoad);
      // Ensure that we have the instance tree down the line so that we can colorize the models
      await viewer?.waitForLoadDone({
        geometry: true,
        textures: false,
        propDb: true,
      });
      // need to set state for the now currently loaded models to easily unload them later
      // first need to figure out which models have now been removed
      // We wanna make sure not to trigger a re-render so we edit the reference that we have
      const tempModelEntries = Object.entries(tempModels);
      for (const [urn, modelInstance] of tempModelEntries) {
        // I am certain that the entries in tempModels are not in the tempModelOverlaysRef
        tempModelOverlaysRef.current[urn] = modelInstance;
      }
      return { ...loadedModels, ...tempModels };
    },
    [loadOverlayModels, viewer]
  );

  // Takes in an array of orders and returns them resolved
  const resolveOrdersForEvent = useCallback(
    async (
      state: RawEventState,
      provisionalLoadedModels: Record<string, Autodesk.Viewing.Model>,
      // in history, we shouldn't write to cache by default, we only allow this for the current version
      shouldWriteToCache = false
    ): Promise<Array<ResolvedOrder>> => {
      const { project } = state;
      const pool = getOrMakePool();
      const { errors, results } = await runWorkInBatches(
        project.orders,
        async (order) => {
          const polygonCount =
            project.settings?.polygonCalculationLimit ?? 5000;
          const unitSettings = project.settings?.unitSettings ?? undefined;
          const viewerGlobOffset = project.settings?.viewerGlobalOffset;
          const globalOffset = [
            viewerGlobOffset?.x ?? 0,
            viewerGlobOffset?.y ?? 0,
            viewerGlobOffset?.z ?? 0,
          ] as Point;
          const ctxToUse = {
            ...state.propertyContext,
            loadedModels: provisionalLoadedModels,
          };
          const projectSettings = {
            unitSettings,
            globalOffset,
            maxTrianglesForComplexAlgorithm: polygonCount,
          };
          return resolveOrderV2(
            pool,
            projectId,
            order,
            ctxToUse,
            true,
            false,
            projectSettings,
            shouldWriteToCache
          );
        }
      );

      // TODO: Handle errors
      if (errors.length > 0) {
        console.error('Errors during resolving: ', errors);
      }
      return results.map((rst) => {
        return {
          ...rst.resolvedOrder,
          orderEntries: rst.resolvedOrder.orderEntries.map(shaveOffDatapoints),
        };
      });
    },
    []
  );

  const getResolvedStateOfEventRaw = useCallback(
    async (historyEvent: HistoryEventDeepFragment) => {
      // 1. Fetch the state from s3
      const stateUrl = await getStateUrl(historyEvent.stateKey);
      // const stateString = await fetchAndParseS3State
      const stateString = await fetchS3StateString(stateUrl);
      const stateFromS3 = JSON.parse(stateString);

      // 2. Load the models
      const provisionalLoadedModels = await prepModels(
        stateFromS3.propertyContext.loadedModels
      );
      // 2. if resolved is true on event, return the state from s3.
      if (historyEvent.resolved) {
        return stateFromS3;
      }
      // 3. otherwise, resolve the data for event using the state from s3
      const resolvedOrders = await resolveOrdersForEvent(
        stateFromS3,
        provisionalLoadedModels
      );
      // 4. add the resolved state to s3
      const key = historyEvent.stateKey.replace('unresolved', 'resolved');
      const stateData = {
        propertyContext: stateFromS3.propertyContext,
        activeModels: stateFromS3.activeModels,
        project: {
          ...stateFromS3.project,
          orders: resolvedOrders,
        },
      };
      await sendToS3(key, s3Client, stateData);
      // 5. update the db telling it that the state has been resolved and update the state key
      await updateHistoryEvent({
        input: {
          id: historyEvent.id,
          patch: { resolved: true, stateKey: key },
        },
      });
      // 6. delete the unresolved state from s3; we can do this in parallel with the previous request
      await deleteS3State(historyEvent.stateKey);
      // 7. return the resolved state to be used for diffing
      return stateData;
    },
    [
      s3Client,
      getStateUrl,
      fetchS3StateString,
      resolveOrdersForEvent,
      prepModels,
    ]
  );

  const getResolvedStateOfEvent = useCallback(
    async (historyEvent: HistoryEventDeepFragment) => {
      const op = async () => {
        return getResolvedStateOfEventRaw(historyEvent);
      };
      const { valuePromise } = dedupeOperations(historyEvent.stateKey, op);
      return valuePromise;
    },
    [getResolvedStateOfEventRaw]
  );

  const resolveCurrentProjectLazily = useCallback(
    async (project: ProjectDeepFragment) => {
      // Can you be in both current and temp? No
      const modelUrnsInTemp = new Set(
        Object.keys(tempModelOverlaysRef.current)
      );
      const modelUrnsOnlyInCurrent = [];
      const newLoadedModels: ModelsMap = {};
      for (const urnInCurrent of Object.keys(propCtxRef.current.loadedModels)) {
        if (!modelUrnsInTemp.has(urnInCurrent)) {
          modelUrnsOnlyInCurrent.push(urnInCurrent);
          newLoadedModels[urnInCurrent] =
            propCtxRef.current.loadedModels[urnInCurrent];
        }
      }

      const pctx = {
        ...propCtxRef.current,
        loadedModels: modelUrnsOnlyInCurrent,
      };

      const ordersForProject = await resolveOrdersForEvent(
        { project, propertyContext: pctx },
        newLoadedModels,
        true
      );
      return {
        project: {
          ...project,
          orders: ordersForProject,
        },
        activeModels,
        propertyContext: pctx,
      };
    },
    [resolveOrdersForEvent, activeModels]
  );

  // Ensure to unload loaded models when you exit from the event history component
  const unloadOverlayModels = useCallback(async () => {
    const modelsToUnload = Object.values(tempModelOverlaysRef.current);
    modelsToUnload.forEach((model) => unloadOverlayModel(model));
    tempModelOverlaysRef.current = {};
  }, [unloadOverlayModel]);

  const unloadModels = useCallback(
    (models: Array<Autodesk.Viewing.Model>) => {
      for (const model of models) {
        unloadOverlayModel(model);
      }
    },
    [unloadOverlayModel]
  );

  return {
    getResolvedStateOfEvent,
    unloadOverlayModels,
    unloadModels,
    clearColors,
    resolveCurrentProjectLazily,
  };
}
